import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { NextStep } from '@shared/models/next-step';
import { LoadingState } from '@shared/types/loading-state';
import { createNextStep, createNextStepFailure, createNextStepSuccess } from '../actions/create-next-step.actions';
import { loadNextSteps, loadNextStepsFailure, loadNextStepsSuccess } from '../actions/load-next-steps.actions';
import { loadNextStepSuccess } from '../actions/load-next-step.actions';
import { setNextStepsFilter } from '../actions/set-next-steps-filter.actions';
import { PaginationState } from '@shared/models/pagination-state';
import { selectSession } from '@shared/reducers/session.reducer';
import { NextStepFilters } from '@shared/models/next-step-filters';

export const featureKey = 'next-steps';

export const nextStepsAdapter = createEntityAdapter<NextStep>({
  selectId: (nextStep: NextStep) => nextStep.attributes.id,
});

export interface State extends EntityState<NextStep> {
  loadingState: LoadingState;
  filters?: NextStepFilters;
  paginationState?: PaginationState;
  loadingText?: string;
  nextStepsFilter: NextStepFilters;
}

export const initialState: State = nextStepsAdapter.getInitialState({
  loadingState: 'not_loading' as LoadingState,
  nextStepsFilter: {},
});

export const nextStepsReducer = createReducer(
  initialState,
  on(
    loadNextSteps,
    (state, { filters }): State => ({
      ...state,
      filters: filters,
      loadingState: 'loading' as LoadingState,
      loadingText: 'Loading next steps...',
    }),
  ),
  on(
    createNextStep,
    (state): State => ({
      ...state,
      loadingState: 'loading' as LoadingState,
      loadingText: 'Creating next step...',
    }),
  ),
  on(createNextStepSuccess, loadNextStepSuccess, (state, { nextStep }): State => {
    if (!nextStep) return state;

    return nextStepsAdapter.upsertOne(nextStep, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(loadNextStepsSuccess, (state, { nextSteps, paginationState }): State => {
    return nextStepsAdapter.upsertMany(nextSteps, {
      ...state,
      paginationState: paginationState,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(
    createNextStepFailure,
    loadNextStepsFailure,
    (state): State => ({
      ...state,
      loadingState: 'not_loading' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(
    setNextStepsFilter,
    (state, { filters }): State => ({
      ...state,
      nextStepsFilter: filters,
    }),
  ),
);

export function reducer(state: State | undefined, action: any) {
  return nextStepsReducer(state, action);
}

export const nextStepsFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll } = nextStepsAdapter.getSelectors(nextStepsFeatureSelector);

export const selectAllNextSteps = selectAll;

export const selectNextStepsLoadingState = createSelector(
  nextStepsFeatureSelector,
  (state: State) => state.loadingState,
);

export const selectNextStepsPaginationState = createSelector(
  nextStepsFeatureSelector,
  (state) => state.paginationState,
);

export const selectNextStepsLoadingText = createSelector(nextStepsFeatureSelector, (state: State) => state.loadingText);

export const selectNextStep = (leadId?: number, groupId?: string, opportunityId?: number) =>
  createSelector(selectAllNextSteps, (nextSteps: NextStep[]) =>
    nextSteps.find((nextStep) =>
      leadId !== undefined
        ? nextStep.attributes.lead_id === leadId
        : opportunityId !== undefined
          ? nextStep.attributes.opportunity_id === opportunityId && nextStep.attributes.group_id === groupId
          : nextStep.attributes.lead_id === null &&
            nextStep.attributes.opportunity_id === null &&
            nextStep.attributes.group_id === groupId,
    ),
  );

export const selectSessionUserNextSteps = createSelector(
  nextStepsFeatureSelector,
  selectSession,
  selectAllNextSteps,
  (state: State, session, nextSteps: NextStep[]) =>
    nextSteps
      .filter(
        (nextStep) =>
          nextStep.attributes.owner_id == session?.id &&
          (!state.filters?.next_step_context
            ? true
            : state.filters.next_step_context == 'opportunity'
              ? nextStep.attributes.opportunity_id !== null
              : state.filters.next_step_context == 'lead'
                ? nextStep.attributes.lead_id !== null
                : nextStep.attributes.opportunity_id === null && nextStep.attributes.lead_id === null),
      )
      .sort((a, b) => new Date(a.attributes.due_date).getTime() - new Date(b.attributes.due_date).getTime()),
);

export const selectNextStepsFilter = createSelector(nextStepsFeatureSelector, (state: State) => state.nextStepsFilter);

export const selectFilteredSessionUserNextSteps = createSelector(
  selectSessionUserNextSteps,
  selectNextStepsFilter,
  (nextSteps: NextStep[], filters: NextStepFilters) =>
    nextSteps.filter((nextStep) => {
      if (filters.opportunity_id) {
        if (nextStep.attributes.opportunity_id !== filters.opportunity_id) {
          return false;
        }
      }

      if (filters.lead_id) {
        if (nextStep.attributes.lead_id !== filters.lead_id) {
          return false;
        }
      }

      if (filters.from_due_date || filters.to_due_date) {
        const dueDate = new Date(nextStep.attributes.due_date);
        if (filters.from_due_date) {
          const fromDate = new Date(filters.from_due_date);
          if (dueDate < fromDate) {
            return false;
          }
        }
        if (filters.to_due_date) {
          const toDate = new Date(filters.to_due_date);
          if (dueDate > toDate) {
            return false;
          }
        }
      }

      if (filters.group_type) {
        if (nextStep.attributes.group_type !== filters.group_type) {
          return false;
        }
      }

      if (filters.next_step_context) {
        const context = filters.next_step_context;
        const hasOpportunity = nextStep.attributes.opportunity_id !== null;
        const hasLead = nextStep.attributes.lead_id !== null;
        if (
          (context === 'general' && (hasOpportunity || hasLead)) ||
          (context === 'opportunity' && !hasOpportunity) ||
          (context === 'lead' && !hasLead)
        ) {
          return false;
        }
      }

      return true;
    }),
);
